/**
 * One employee: their details, the SSN on file, and their W-4s (spec §18.3).
 *
 * THE SSN IS SHOWN AS ITS LAST FOUR DIGITS AND NOTHING ELSE. The API sends no
 * more, so this screen could not show more if it tried. Replacing it is its own
 * small form and never part of Edit Details: a details form carrying an SSN
 * field would send it blank with every change of address, and blank removes
 * the number.
 *
 * AN UNREADABLE SSN IS STATED, NOT HIDDEN. It means the server holds no key
 * for the version the value names -- a rotation's mistake -- and this is where
 * that shows first, so the sentence names the version and both variables. It
 * is a standing fact, so a notice, never a toast.
 *
 * A W-4 IS NEVER EDITED (migration 0031). The list offers Record a W-4 and
 * Remove, and nothing else: a typo is removed and the form entered again, and a
 * new W-4 simply takes effect on its own date. Removing asks first, inline, the
 * Settings screen's way.
 *
 * PAY IS A DATED HISTORY, AND SO ARE THE OPENING TOTALS (stage 3). A pay rate
 * is never edited: a raise is a new rate taking effect on its own date, and a
 * mistake is removed and entered again, exactly as a W-4 is. The opening totals
 * are the pay this module did not run -- for 2026, the quarters paid by hand --
 * and every wage base counts from them, so they belong in before the year's
 * first run. Both are kept once a run has used them, and the refusal says which
 * run.
 *
 * DELETE EMPLOYEE is for one entered by mistake or to test (the operator,
 * 2026-09-15), and it takes their W-4s, pay rates and opening totals with it. It sits at the foot of the
 * record, apart from Edit Details, and asks first, inline, as Delete Entry does:
 * the question says what is lost, and that backups already taken keep a copy.
 */

import { AccessNotice } from '../components/AccessNotice.js';
import { useBusiness, useCan } from '../auth/AuthProvider.js';
import { EmployeeAllocation } from '../components/EmployeeAllocation.js';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { EmployeeForm } from '../components/EmployeeForm.js';
import { Toast } from '../components/Toast.js';
import { W4Lines, W4Wizard } from '../components/W4Wizard.js';
import {
  ApiError,
  api,
  type CreateEmployeeBody,
  type Employee,
  type OpeningTotalsAmounts,
  type PayFrequency,
  type RecordW4Body,
} from '../api.js';
import { addressLines, employeeName, maskSsn, periodWord } from '../lib/labels.js';
import { formatCents, normaliseAmountInput, parseCents } from '../lib/money.js';

/** Each opening total, and what it is called on screen. */
const OPENING_TOTALS_FIELDS: Array<[keyof OpeningTotalsAmounts, string]> = [
  ['federalIncomeTaxWagesCents', 'Federal Income Tax Wages'],
  ['federalIncomeTaxWithheldCents', 'Federal Income Tax Withheld'],
  ['socialSecurityWagesCents', 'Social Security Wages'],
  ['socialSecurityTaxCents', 'Social Security Withheld'],
  ['medicareWagesCents', 'Medicare Wages'],
  ['medicareTaxCents', 'Medicare Withheld'],
  ['additionalMedicareTaxCents', 'Additional Medicare Withheld'],
  ['futaWagesCents', 'FUTA Wages'],
  ['texasUnemploymentWagesCents', 'Texas Unemployment Wages'],
  ['healthPremiumCents', 'Health Insurance Premiums'],
  ['supplementalWagesCents', 'Bonuses and Other Supplemental Wages'],
];

const BLANK_TOTALS: Record<string, string> = Object.fromEntries(
  OPENING_TOTALS_FIELDS.map(([key]) => [key, '']),
);

export function EmployeePage() {
  const mayRun = useCan()('run_payroll');
  // Only an S corporation has an owner-officer (0083); an unrecorded type shows it, as before.
  const business = useBusiness();
  const asksOwnerOfficer = business == null || business.taxStatus == null || business.taxStatus === 's_corporation';
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const [enteringSsn, setEnteringSsn] = useState(false);
  const [ssnText, setSsnText] = useState('');
  const [ssnError, setSsnError] = useState<string | null>(null);

  const [recording, setRecording] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(null);
  const [w4Error, setW4Error] = useState<string | null>(null);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [addingRate, setAddingRate] = useState(false);
  const [rateEffectiveOn, setRateEffectiveOn] = useState('');
  const [rateFrequency, setRateFrequency] = useState<PayFrequency>('quarterly');
  const [rateAmount, setRateAmount] = useState('');
  const [rateError, setRateError] = useState<string | null>(null);
  const [removingRate, setRemovingRate] = useState<string | null>(null);

  const [addingTotals, setAddingTotals] = useState(false);
  const [totalsYear, setTotalsYear] = useState('');
  const [totalsThrough, setTotalsThrough] = useState('');
  const [totalsAmounts, setTotalsAmounts] = useState<Record<string, string>>(BLANK_TOTALS);
  const [totalsError, setTotalsError] = useState<string | null>(null);
  const [removingTotals, setRemovingTotals] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.getEmployee(id);
      setEmployee(result.employee);
      setLoadError(null);
    } catch (caught) {
      setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the employee.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveDetails(body: CreateEmployeeBody) {
    setBusy(true);
    setDetailsError(null);
    try {
      const result = await api.updateEmployee(id, body);
      setEmployee(result.employee);
      setEditing(false);
      setNotice('Details saved');
    } catch (caught) {
      setDetailsError(caught instanceof ApiError ? caught.message : 'Could not save the details.');
    } finally {
      setBusy(false);
    }
  }

  async function saveSsn() {
    setBusy(true);
    setSsnError(null);
    try {
      const result = await api.updateEmployee(id, { ssn: ssnText });
      setEmployee(result.employee);
      setEnteringSsn(false);
      setSsnText('');
      setNotice('SSN saved');
    } catch (caught) {
      setSsnError(caught instanceof ApiError ? caught.message : 'Could not save the SSN.');
    } finally {
      setBusy(false);
    }
  }

  /** Throws the refusal back to the wizard, which shows it beside the answers. */
  async function saveW4(body: RecordW4Body) {
    await api.recordW4(id, body);
    setRecording(false);
    setNotice('W-4 recorded');
    await load();
  }

  async function removeW4(w4Id: string) {
    setBusy(true);
    setW4Error(null);
    try {
      await api.removeW4(w4Id);
      setConfirmingRemoval(null);
      setNotice('W-4 removed');
      await load();
    } catch (caught) {
      setW4Error(caught instanceof ApiError ? caught.message : 'Could not remove the W-4.');
    } finally {
      setBusy(false);
    }
  }

  /** Back to the list once deleted: there is no employee left to show. */
  async function deleteEmployee() {
    setBusy(true);
    setDeleteError(null);
    try {
      await api.deleteEmployee(id);
      navigate('/payroll');
    } catch (caught) {
      setConfirmingDelete(false);
      setDeleteError(caught instanceof ApiError ? caught.message : 'Could not delete the employee.');
      setBusy(false);
    }
  }

  /** A raise is a new rate; the refusal for a rate a run used names that run. */
  async function savePayRate() {
    if (rateEffectiveOn === '') {
      setRateError('Enter the first pay date at this rate.');
      return;
    }
    const cents = parseCents(rateAmount);
    if (cents === null || cents <= 0) {
      setRateError('The pay each period is an amount above zero.');
      return;
    }
    setBusy(true);
    setRateError(null);
    try {
      await api.recordPayRate(id, {
        effectiveOn: rateEffectiveOn,
        frequency: rateFrequency,
        amountPerPeriodCents: cents,
      });
      setAddingRate(false);
      setRateEffectiveOn('');
      setRateAmount('');
      setNotice('Pay rate recorded');
      await load();
    } catch (caught) {
      setRateError(caught instanceof ApiError ? caught.message : 'Could not record the pay rate.');
    } finally {
      setBusy(false);
    }
  }

  async function removePayRate(rateId: string) {
    setBusy(true);
    setRateError(null);
    try {
      await api.removePayRate(rateId);
      setRemovingRate(null);
      setNotice('Pay rate removed');
      await load();
    } catch (caught) {
      setRemovingRate(null);
      setRateError(caught instanceof ApiError ? caught.message : 'Could not remove the pay rate.');
    } finally {
      setBusy(false);
    }
  }

  async function saveOpeningTotals() {
    const year = Number(totalsYear);
    if (!Number.isInteger(year) || year < 2020) {
      setTotalsError('Opening totals are for a calendar year from 2020 on.');
      return;
    }
    if (totalsThrough === '') {
      setTotalsError('Enter the last pay date the totals include.');
      return;
    }
    const amounts: Record<string, number> = {};
    for (const [key, label] of OPENING_TOTALS_FIELDS) {
      const cents = parseCents(totalsAmounts[key] ?? '');
      if (cents === null) {
        setTotalsError(`${label} is not an amount.`);
        return;
      }
      amounts[key] = cents;
    }
    setBusy(true);
    setTotalsError(null);
    try {
      await api.recordOpeningTotals(id, {
        year,
        throughDate: totalsThrough,
        ...(amounts as unknown as OpeningTotalsAmounts),
      });
      setAddingTotals(false);
      setTotalsYear('');
      setTotalsThrough('');
      setTotalsAmounts(BLANK_TOTALS);
      setNotice('Opening totals recorded');
      await load();
    } catch (caught) {
      setTotalsError(caught instanceof ApiError ? caught.message : 'Could not record the opening totals.');
    } finally {
      setBusy(false);
    }
  }

  async function removeOpeningTotals(totalsId: string) {
    setBusy(true);
    setTotalsError(null);
    try {
      await api.removeOpeningTotals(totalsId);
      setRemovingTotals(null);
      setNotice('Opening totals removed');
      await load();
    } catch (caught) {
      setRemovingTotals(null);
      setTotalsError(caught instanceof ApiError ? caught.message : 'Could not remove the opening totals.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <section className="payroll">
        <p>
          <Link to="/payroll">Back to Payroll</Link>
        </p>
        <p role="alert" className="error">
          {loadError}
        </p>
      </section>
    );
  }
  if (!employee) return <p className="muted">Loading…</p>;

  const address = addressLines(employee);
  const ssnAction = employee.ssn.status === 'none' ? 'Add SSN' : 'Replace SSN';
  /** The latest rate taking effect on or before the tenant's today, as the API orders them. */
  const currentPayRateId = employee.payRates.find((rate) => rate.effectiveOn <= employee.today)?.id ?? null;

  return (
    <section className="payroll">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>{employeeName(employee)}</h1>
      <AccessNotice capability="run_payroll" what="employees" />
      {notice && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}

      <section className="settings-section" aria-labelledby="employee-details">
        <h2 id="employee-details">Details</h2>
        {detailsError && (
          <p role="alert" className="error">
            {detailsError}
          </p>
        )}
        {editing ? (
          <EmployeeForm
            label="Edit Details"
            initial={employee}
            withSsn={false}
            submitLabel="Save Details"
            busy={busy}
            onSubmit={(body) => void saveDetails(body)}
            onCancel={() => {
              setEditing(false);
              setDetailsError(null);
            }}
          />
        ) : (
          <>
            <dl className="record-facts">
              <dt>Address</dt>
              <dd>
                {address.length === 0
                  ? 'Not on file'
                  : address.map((line) => (
                      <span key={line} className="address-line">
                        {line}
                      </span>
                    ))}
              </dd>
              <dt>Hire Date</dt>
              <dd>{employee.hireDate}</dd>
              {asksOwnerOfficer && (
                <>
                  <dt>Owner-Officer</dt>
                  <dd>{employee.isOwnerOfficer ? 'Yes' : 'No'}</dd>
                </>
              )}
              <dt>Officer</dt>
              <dd>{employee.isOfficer ? 'Yes' : 'No'}</dd>
            </dl>
            {mayRun && (
              <button type="button" className="link" onClick={() => setEditing(true)}>
                Edit Details
              </button>
            )}
          </>
        )}
      </section>

      <section className="settings-section" aria-labelledby="employee-ssn">
        <h2 id="employee-ssn">SSN</h2>
        {employee.ssn.status === 'on_file' && <p className="ssn">{maskSsn(employee.ssn.lastFour)}</p>}
        {employee.ssn.status === 'none' && <p className="muted">Not on file.</p>}
        {employee.ssn.status === 'withheld' && (
          <p className="muted">On file. Its digits are shown only to someone who may see tax ids.</p>
        )}
        {employee.ssn.status === 'unreadable' && (
          <p className="notice notice-warn">
            An SSN is stored, but this server holds no key that reads it
            {employee.ssn.keyVersion === null ? '' : ` (key v${employee.ssn.keyVersion})`}. Check
            PII_ENCRYPTION_KEY and PII_ENCRYPTION_KEY_PREVIOUS, or enter the number again.
          </p>
        )}
        {ssnError && (
          <p role="alert" className="error">
            {ssnError}
          </p>
        )}
        {enteringSsn ? (
          <div className="new-account" role="group" aria-label={ssnAction}>
            <label className="field">
              <span>SSN</span>
              <input
                value={ssnText}
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setSsnText(event.target.value)}
              />
            </label>
            <div className="row">
              <button type="button" disabled={busy || ssnText.trim() === ''} onClick={() => void saveSsn()}>
                Save SSN
              </button>
              <button
                type="button"
                className="link"
                onClick={() => {
                  setEnteringSsn(false);
                  setSsnText('');
                  setSsnError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : mayRun ? (
          <button type="button" className="link" onClick={() => setEnteringSsn(true)}>
            {ssnAction}
          </button>
        ) : null}
      </section>

      <section className="settings-section" aria-labelledby="employee-w4">
        <h2 id="employee-w4">W-4</h2>
        {w4Error && (
          <p role="alert" className="error">
            {w4Error}
          </p>
        )}
        {recording ? (
          <W4Wizard employee={employee} onSave={saveW4} onCancel={() => setRecording(false)} />
        ) : mayRun ? (
          <button type="button" onClick={() => setRecording(true)}>
            Record a W-4
          </button>
        ) : null}

        {employee.w4s.length === 0 ? (
          <p className="muted">No W-4 on file.</p>
        ) : (
          <ol className="history-list" aria-label="W-4s">
            {employee.w4s.map((w4) => (
              <li key={w4.id}>
                <p>
                  <strong>Takes Effect {w4.effectiveOn}</strong>{' '}
                  {employee.currentW4?.id === w4.id && <span className="tag">In Effect</span>}
                  {w4.effectiveOn > employee.today && <span className="tag">Upcoming</span>}
                </p>
                <W4Lines w4={w4} />
                <p className="muted history-when">
                  Form W-4 ({w4.formYear}) · signed {w4.signedOn} · recorded by{' '}
                  {w4.recordedByEmail ?? 'no user recorded'}
                </p>
                {confirmingRemoval === w4.id ? (
                  <div className="new-account undo-form" role="group" aria-label="Remove This W-4">
                    <p>
                      Remove this W-4? It is deleted, not kept, so use this for a form entered by
                      mistake. To correct a typo, remove it and record the form again.
                    </p>
                    <div className="row">
                      <button type="button" disabled={busy} onClick={() => void removeW4(w4.id)}>
                        Remove It
                      </button>
                      <button type="button" className="link" onClick={() => setConfirmingRemoval(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : !mayRun ? null : (
                  <button
                    type="button"
                    className="link"
                    aria-label={`Remove the W-4 taking effect ${w4.effectiveOn}`}
                    onClick={() => setConfirmingRemoval(w4.id)}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="settings-section" aria-labelledby="employee-pay">
        <h2 id="employee-pay">Pay</h2>
        {rateError && (
          <p role="alert" className="error">
            {rateError}
          </p>
        )}
        {addingRate ? (
          <div className="new-account" role="group" aria-label="Record a Pay Rate">
            <div className="field-row">
              <label className="field field-medium">
                <span>Takes Effect On</span>
                <input
                  type="date"
                  value={rateEffectiveOn}
                  onChange={(event) => setRateEffectiveOn(event.target.value)}
                />
              </label>
              <label className="field field-medium">
                <span>How Often</span>
                <select
                  value={rateFrequency}
                  onChange={(event) => setRateFrequency(event.target.value as PayFrequency)}
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every Two Weeks</option>
                  <option value="monthly">Monthly</option>
                  {employee.isOwnerOfficer && <option value="quarterly">Quarterly</option>}
                </select>
              </label>
              <label className="field field-medium">
                <span>Pay Each Period</span>
                {/*
                  Tidied when the operator leaves the field, the same way the
                  editor's amount columns and the W-4 wizard already do it.
                  `normaliseAmountInput` leaves an unparseable value EXACTLY as
                  typed, so a mistake stays visible and points at what was
                  entered, and leaves an empty field empty rather than writing
                  0.00 into a box nobody filled in.
                */}
                <input
                  inputMode="decimal"
                  value={rateAmount}
                  onChange={(event) => setRateAmount(event.target.value)}
                  onBlur={() => setRateAmount(normaliseAmountInput(rateAmount))}
                />
              </label>
            </div>
            <div className="row">
              <button type="button" disabled={busy} onClick={() => void savePayRate()}>
                Save Pay Rate
              </button>
              <button
                type="button"
                className="link"
                onClick={() => {
                  setAddingRate(false);
                  setRateError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : mayRun ? (
          <button type="button" onClick={() => setAddingRate(true)}>
            Record a Pay Rate
          </button>
        ) : null}

        {employee.payRates.length === 0 ? (
          <p className="muted">
            No pay rate on file. A run needs one taking effect on or before its pay date.
          </p>
        ) : (
          <ol className="history-list" aria-label="Pay Rates">
            {employee.payRates.map((rate) => (
              <li key={rate.id}>
                <p>
                  <strong>Takes Effect {rate.effectiveOn}</strong>{' '}
                  {rate.id === currentPayRateId && <span className="tag">In Effect</span>}
                  {rate.effectiveOn > employee.today && <span className="tag">Upcoming</span>}
                </p>
                <p>
                  {formatCents(rate.amountPerPeriodCents)} each {periodWord(rate.frequency)}
                </p>
                <p className="muted history-when">
                  recorded by {rate.recordedByEmail ?? 'no user recorded'}
                </p>
                {removingRate === rate.id ? (
                  <div className="new-account undo-form" role="group" aria-label="Remove This Pay Rate">
                    <p>
                      Remove this pay rate? It is deleted, not kept. A raise is a new rate taking
                      effect on its own date, which leaves this one as the rate past runs were paid
                      at.
                    </p>
                    <div className="row">
                      <button type="button" disabled={busy} onClick={() => void removePayRate(rate.id)}>
                        Remove It
                      </button>
                      <button type="button" className="link" onClick={() => setRemovingRate(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : !mayRun ? null : (
                  <button
                    type="button"
                    className="link"
                    aria-label={`Remove the pay rate taking effect ${rate.effectiveOn}`}
                    onClick={() => setRemovingRate(rate.id)}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      <EmployeeAllocation
        employeeId={employee.id}
        employeeName={employeeName(employee)}
        allocation={employee.allocation}
        mayChange={mayRun}
        onSaved={(allocation) => {
          setEmployee({ ...employee, allocation });
          setNotice('Allocation saved');
        }}
      />

      <section className="settings-section" aria-labelledby="employee-opening">
        <h2 id="employee-opening">Opening Totals</h2>
        <p className="muted">
          The pay this module did not run — for 2026, the quarters paid by hand. Every wage base
          counts from these, so they go in before the year's first run, and wages are counted before
          any limit.
        </p>
        {totalsError && (
          <p role="alert" className="error">
            {totalsError}
          </p>
        )}
        {addingTotals ? (
          <div className="new-account" role="group" aria-label="Enter Opening Totals">
            <div className="field-row">
              <label className="field field-short">
                <span>Year</span>
                <input
                  inputMode="numeric"
                  value={totalsYear}
                  onChange={(event) => setTotalsYear(event.target.value)}
                />
              </label>
              <label className="field field-medium">
                <span>Including Pay Through</span>
                <input
                  type="date"
                  value={totalsThrough}
                  onChange={(event) => setTotalsThrough(event.target.value)}
                />
              </label>
            </div>
            <div className="employee-fields">
              {OPENING_TOTALS_FIELDS.map(([key, label]) => (
                <label key={key} className="field">
                  <span>{label}</span>
                  {/*
                    Eleven figures copied off a 941 is the longest stretch of
                    typing in the application, and the place a stray "2296.3"
                    is least likely to be noticed -- it reads as $2,296.03 to
                    anyone scanning, and these rows cannot be edited once a run
                    has used them. Formatting on blur makes the cents explicit
                    while the field is still in front of the operator.
                  */}
                  <input
                    inputMode="decimal"
                    value={totalsAmounts[key] ?? ''}
                    onChange={(event) =>
                      setTotalsAmounts({ ...totalsAmounts, [key]: event.target.value })
                    }
                    onBlur={() =>
                      setTotalsAmounts({
                        ...totalsAmounts,
                        [key]: normaliseAmountInput(totalsAmounts[key] ?? ''),
                      })
                    }
                  />
                </label>
              ))}
            </div>
            <div className="row">
              <button type="button" disabled={busy} onClick={() => void saveOpeningTotals()}>
                Save Opening Totals
              </button>
              <button
                type="button"
                className="link"
                onClick={() => {
                  setAddingTotals(false);
                  setTotalsError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : mayRun ? (
          <button type="button" onClick={() => setAddingTotals(true)}>
            Enter Opening Totals
          </button>
        ) : null}

        {employee.openingTotals.length === 0 ? (
          <p className="muted">No opening totals on file.</p>
        ) : (
          <ol className="history-list" aria-label="Opening Totals">
            {employee.openingTotals.map((totals) => (
              <li key={totals.id}>
                <p>
                  <strong>{totals.year}</strong> · including pay through {totals.throughDate}
                </p>
                <dl className="record-facts">
                  {OPENING_TOTALS_FIELDS.map(([key, label]) => (
                    <div key={key}>
                      <dt>{label}</dt>
                      <dd>{formatCents(totals[key])}</dd>
                    </div>
                  ))}
                </dl>
                <p className="muted history-when">
                  recorded by {totals.recordedByEmail ?? 'no user recorded'}
                </p>
                {removingTotals === totals.id ? (
                  <div className="new-account undo-form" role="group" aria-label="Remove These Opening Totals">
                    <p>
                      Remove these opening totals? They are deleted, not kept. A run that already
                      counted from them keeps them instead.
                    </p>
                    <div className="row">
                      <button type="button" disabled={busy} onClick={() => void removeOpeningTotals(totals.id)}>
                        Remove It
                      </button>
                      <button type="button" className="link" onClick={() => setRemovingTotals(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : !mayRun ? null : (
                  <button
                    type="button"
                    className="link"
                    aria-label={`Remove the opening totals for ${totals.year}`}
                    onClick={() => setRemovingTotals(totals.id)}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      {mayRun && (
        <div className="settings-section">
          {deleteError && (
            <p role="alert" className="error">
              {deleteError}
            </p>
          )}
          {confirmingDelete ? (
            <div className="new-account undo-form" role="group" aria-label="Delete This Employee">
              <p>
                Delete this employee? Their details, SSN, W-4s, pay rates and opening totals are
                deleted, not kept. Backups already taken still hold a copy. An employee payroll has
                paid is kept instead, because the runs name who was paid.
              </p>
              <div className="row">
                <button type="button" disabled={busy} onClick={() => void deleteEmployee()}>
                  Delete It
                </button>
                <button type="button" className="link" onClick={() => setConfirmingDelete(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="link"
              onClick={() => {
                setDeleteError(null);
                setConfirmingDelete(true);
              }}
            >
              Delete Employee
            </button>
          )}
        </div>
      )}
    </section>
  );
}
